import { useUser } from '@/hooks/use-user';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import type { UserWithCommunities } from '@/types';
import { resolveCommunityLandingPath } from '@/lib/community-navigation';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  allowedPermissions?: string[];
}

export function RoleGuard({ children, allowedRoles, allowedPermissions }: RoleGuardProps) {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const { communities, isLoading, user } = useUser();

  const currentCommunity = communities.find((c) => c.id === communityId) as
    | UserWithCommunities['communities'][number]
    | undefined;

  const userRoles = currentCommunity?.roles || [];

  const hasRole =
    allowedRoles?.some((role) => userRoles.includes(role as (typeof userRoles)[number])) ?? false;
  const hasPermission =
    allowedPermissions?.some((permission) =>
      currentCommunity?.permissions.includes(
        permission as (typeof currentCommunity.permissions)[number]
      )
    ) ?? false;
  const allowed = hasRole || hasPermission;

  useEffect(() => {
    if (!isLoading && user && !currentCommunity) {
      navigate('/dashboard', { replace: true });
    } else if (!isLoading && user && currentCommunity && !allowed) {
      navigate(resolveCommunityLandingPath(currentCommunity, user), { replace: true });
    }
  }, [isLoading, user, currentCommunity, allowed, navigate, communityId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return <>{children}</>;
}
